import { ITextField } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPActionButton,
  IPMessageBar,
  IPMessageBarButton,
  IPMessageBarType,
  IPPrimaryButton,
  IPSeparator,
  IPText,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ActionCallout } from 'components/CallOuts/ActionCallout';
import RichTextEditor, { IRichTextEditor } from 'components/Text/RichTextEditor';
import { globalStackTokensGapSmall, newIcon } from 'globalStyles';
import React, { createRef, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { textToHtmlParagraphs } from 'utils/html';
import { getRandomId } from 'utils/string';

interface IAddNewCheckListItem {
  allowEdit: boolean;
  singleLineEdit?: boolean;
  compact?: boolean;
  onAddNewCheckListItem: (newCheckListDescription: string) => void;
  // Adds several items at once; used for a pasted list (see pasteAsList)
  onAddNewCheckListItems?: (newCheckListDescriptions: string[]) => void;
  // When a pasted text is a list, offer to add it as separate items instead of one
  pasteAsList?: boolean;
  // Reports whether the editor holds text that was not added yet, so the dialog can ask before
  // closing. Given, Escape in the editor also clears that text instead of reaching the dialog.
  onPendingChange?: (hasPendingText: boolean) => void;
}

export interface IAddNewCheckListItemHandle {
  focus: () => void;
}

const maxItemLength = 1024;

export const AddNewCheckListItem = forwardRef(function AddNewCheckListItem(props: IAddNewCheckListItem, ref) {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'translation']);
  const checkInputRef = createRef<ITextField>();
  const [checkInputHasFocus, setCheckInputHasFocus] = useState<boolean>(false);
  const [newCheckListDescription, setNewCheckListDescription] = useState<string>('');
  const [showCompactEditor, setShowCompactEditor] = useState<boolean>(false);
  const editorRef = useRef<IRichTextEditor | null>(null);
  const compactTriggerId = useRef(`checklist-add-${getRandomId()}`);
  const hostId = useRef(`checklist-add-host-${getRandomId()}`);
  const [pastedList, setPastedList] = useState<{ items: string[]; content: string } | undefined>(undefined);

  useImperativeHandle(
    ref,
    (): IAddNewCheckListItemHandle => {
      return {
        focus() {
          if (props.compact) {
            if (props.allowEdit) setShowCompactEditor(true);

            return;
          }
          editorRef.current?.focus();
          //the editor shows as read-only text until it is clicked: click it when TinyMCE is not
          //loaded yet
          const host = document.getElementById(hostId.current);
          if (host && !host.querySelector('.tox-tinymce')) {
            host.querySelector<HTMLElement>('.isoplanner-task-checklist-additem')?.click();
          }
        },
      };
    },
    [props.compact, props.allowEdit],
  );

  const addPastedAsItems = () => {
    if (!pastedList || !props.onAddNewCheckListItems) return;

    const values = pastedList.items.map((item) => textToHtmlParagraphs(item));
    if (values.some((value) => value.length > maxItemLength)) {
      appContext.showNotification(
        t('translation:General.Text.MaxLengthExceeded', { max: maxItemLength }),
        true,
        false,
        true,
      );

      return;
    }
    props.onAddNewCheckListItems(values);
    setPastedList(undefined);
  };

  const addPastedAsOne = async () => {
    if (!pastedList) return;

    // lazy: the cleaner pulls in react-dom/server; the editor has loaded the chunk by the time a list is pasted
    const { cleanAiPastedContent } = await import('utils/aiPasteCleaner');
    const value = newCheckListDescription + cleanAiPastedContent(pastedList.content).html;
    setPastedList(undefined);
    if (value.length > maxItemLength) {
      appContext.showNotification(
        t('translation:General.Text.MaxLengthExceeded', { max: maxItemLength }),
        true,
        false,
        true,
      );

      return;
    }
    setNewCheckListDescription(value);
  };

  const addItem = () => {
    if (!newCheckListDescription || !props.allowEdit) return;

    props.onAddNewCheckListItem(newCheckListDescription);
    setNewCheckListDescription('');
    setShowCompactEditor(false);
  };

  const hasPendingText = newCheckListDescription.trim() !== '';

  const onPendingChangeRef = useRef(props.onPendingChange);
  onPendingChangeRef.current = props.onPendingChange;

  useEffect(() => {
    onPendingChangeRef.current?.(hasPendingText);
  }, [hasPendingText]);

  //an unmounted editor holds no pending text
  useEffect(() => {
    return () => onPendingChangeRef.current?.(false);
  }, []);

  const onKeyDown = (e: Partial<React.KeyboardEvent>) => {
    if (props.onPendingChange && e.key === 'Escape') {
      setNewCheckListDescription('');
      setPastedList(undefined);
      editorRef.current?.blur();
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();

      return;
    }
    if (e.ctrlKey === true && e.key === 'Enter' && newCheckListDescription.length > 0) {
      addItem();
      if (e.preventDefault) e.preventDefault();
    }
  };

  const pasteListBar = pastedList && (
    <IPMessageBar
      messageBarType={IPMessageBarType.info}
      isMultiline={false}
      onDismiss={() => setPastedList(undefined)}
      actions={
        <div>
          <IPMessageBarButton onClick={addPastedAsItems}>
            {t('task:CheckList.PasteList.AddAsItems', { lines: pastedList.items.length })}
          </IPMessageBarButton>
          <IPMessageBarButton onClick={addPastedAsOne}>{t('task:CheckList.PasteList.PasteAsOne')}</IPMessageBarButton>
        </div>
      }
    >
      {t('task:CheckList.PasteList.Message', { lines: pastedList.items.length })}
    </IPMessageBar>
  );

  const richTextEditor = (
    <RichTextEditor
      className="isoplanner-task-checklist-additem"
      ref={editorRef}
      cleanAiPaste
      directEdit={props.compact}
      placeholder={t('task:CheckList.AddPlaceHolder')}
      html={newCheckListDescription}
      maxLength={1024}
      onChange={(value) => setNewCheckListDescription(value ?? '')}
      onAccept={
        props.compact || !appContext.isMobileView
          ? undefined
          : (value, accept) => {
              setNewCheckListDescription('');
              if (accept && value) {
                props.onAddNewCheckListItem(value);
              } else {
                editorRef.current?.blur();
              }
            }
      }
      disallowFullscreen
      maxHeight={300}
      compactToolbar
      onFocus={() => setCheckInputHasFocus(true)}
      onBlur={() => setCheckInputHasFocus(false)}
      onKeyDown={onKeyDown}
      hideRemainingChars
      onPasteList={
        props.pasteAsList && props.onAddNewCheckListItems
          ? (items, content) => setPastedList({ items, content })
          : undefined
      }
    />
  );

  const editor = (
    <>
      {pasteListBar}
      {richTextEditor}
    </>
  );

  if (props.compact) {
    return (
      <React.Fragment>
        <IPActionButton
          id={compactTriggerId.current}
          iconProps={newIcon}
          disabled={!props.allowEdit}
          onClick={() => setShowCompactEditor(true)}
        >
          {t('task:CheckList.CreateNew')}
        </IPActionButton>
        {showCompactEditor && (
          <ActionCallout
            target={`#${compactTriggerId.current}`}
            title={t('task:CheckList.Add')}
            saveText={t('task:CheckList.Add')}
            saveDisabled={!newCheckListDescription || !props.allowEdit}
            width={440}
            setInitialFocus={false}
            hideFooterSeparator
            onSave={addItem}
            onDismiss={() => {
              setNewCheckListDescription('');
              setShowCompactEditor(false);
            }}
          >
            {editor}
          </ActionCallout>
        )}
      </React.Fragment>
    );
  }

  return (
    <IPStack tokens={globalStackTokensGapSmall} horizontal={props.singleLineEdit}>
      {!props.singleLineEdit && <IPSeparator />}
      <IPStack.Item grow id={hostId.current}>
        {editor}
      </IPStack.Item>
      {!appContext.isMobileView && (
        <IPStack.Item>
          <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
            <IPPrimaryButton
              disabled={newCheckListDescription === '' || !props.allowEdit}
              onClick={() => {
                checkInputRef.current?.focus();
                addItem();
              }}
            >
              {t('task:CheckList.Add')}
            </IPPrimaryButton>
            {!props.singleLineEdit && checkInputHasFocus && (
              <IPText variant="small">{t('task:CheckList.KeyTipNew')}</IPText>
            )}
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );
});
